// app/services/ProfileManagementService.js
// Unified service for all professional-side data: profile, resumes, cover letters,
// ATS scoring, AI recommendations, portfolio, skills.
//
// Design principles:
//   - Backend-first, AsyncStorage-cached. Every read tries the server first,
//     falls back to cache. Every write updates cache immediately, then
//     best-effort syncs to backend.
//   - Defensive: never throw out of public methods. Always returns
//     { success: boolean, data?: ..., error?: string }.
//   - Test-aligned: shapes match ProfileManagementService.test.js exactly
//     for runATSTest and buildAIRecommendationsFromATS.

import AsyncStorage from '@react-native-async-storage/async-storage';
import PROF_KEYS from '../constants/profileKeys';

const DEFAULT_API_BASE = 'https://api.jobeng.ai';
const DEFAULT_API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ──────────────────────────────────────────────────────────────────────────────
// Cover letter rendering engines (4 templates)
// ──────────────────────────────────────────────────────────────────────────────
const extractResumeFacts = (resumeData) => {
  const personalInfo = resumeData?.personalInfo || resumeData || {};
  const name =
    personalInfo.name ||
    personalInfo.fullName ||
    [personalInfo.firstName, personalInfo.lastName].filter(Boolean).join(' ') ||
    resumeData?.fullName ||
    resumeData?.name ||
    'Your Name';
  const summary =
    resumeData?.summary ||
    resumeData?.professionalSummary ||
    personalInfo.summary ||
    '';
  const skillsArr = Array.isArray(resumeData?.skills) ? resumeData.skills : [];
  const skills = skillsArr
    .map(s => (typeof s === 'string' ? s : s?.name || s?.title || s?.label))
    .filter(Boolean);
  const experiences = Array.isArray(resumeData?.experience) ? resumeData.experience : [];
  const educations = Array.isArray(resumeData?.education) ? resumeData.education : [];
  const recent = experiences[0] || {};
  const totalYears = experiences.reduce((sum, e) => {
    const start = e.startYear || (e.startDate && parseInt(e.startDate.slice(0, 4), 10));
    const end = e.endYear || (e.endDate && parseInt(e.endDate.slice(0, 4), 10)) || new Date().getFullYear();
    if (start && end && end >= start) return sum + (end - start);
    return sum;
  }, 0);
  return { name, summary, skills, experiences, educations, recent, totalYears };
};

const renderCoverLetterTemplate = (template, resume, ctx) => {
  const { name, summary, skills, recent, totalYears } = resume;
  const { jobTitle, company } = ctx;

  if (template === 'modern') {
    const greeting = company ? `Hi ${company} team,` : 'Hi there,';
    const hook = `When I saw the ${jobTitle} opening${company ? ` at ${company}` : ''}, I knew I had to apply.`;
    const why = recent && recent.title
      ? `At ${recent.company || 'my last role'} I led ${recent.title.toLowerCase()} work that ${recent.description || 'shipped real outcomes'}.${totalYears > 0 ? ` ${totalYears}+ years in this space has taught me what scales.` : ''}`
      : `My background has been about shipping outcomes that move the needle.`;
    const skillsLine = skills.length ? `What I'd bring: ${skills.slice(0, 5).join(' • ')}.` : '';
    return `${greeting}\n\n${hook}\n\n${why}\n\n${skillsLine}\n\nHappy to walk through specifics. Thanks for taking a look.\n\n${name}`;
  }

  if (template === 'creative') {
    const greeting = company ? `Hello ${company},` : 'Hello,';
    const story = summary
      ? `${summary} That's the short version.`
      : `Every great role starts with a story, and here's where mine intersects with yours.`;
    const passion = `Your ${jobTitle} opportunity${company ? ` at ${company}` : ''} caught my eye because it sits at the intersection of what I love doing and what I've spent years getting good at.`;
    const exp = recent && recent.title
      ? `Most recently at ${recent.company || 'my previous company'}, I worked as ${recent.title}.`
      : `Across my career, the moments I'm proudest of are where craft and curiosity overlap.`;
    const skillsLine = skills.length ? `Tools and skills I bring along: ${skills.slice(0, 5).join(', ')}.` : '';
    return `${greeting}\n\n${story}\n\n${passion}\n\n${exp}\n\n${skillsLine}\n\nIf any of this resonates, I'd love to hear more about what you're building.\n\nWarmly,\n${name}`;
  }

  if (template === 'brief') {
    const greeting = company ? `Hi ${company} team,` : 'Hi,';
    const pitch = `I'd like to apply for the ${jobTitle} role${company ? ` at ${company}` : ''}.${
      recent && recent.title ? ` Most recently I was ${recent.title}${recent.company ? ` at ${recent.company}` : ''}.` : ''
    }${skills.length ? ` Skills: ${skills.slice(0, 4).join(', ')}.` : ''}`;
    return `${greeting}\n\n${pitch}\n\nMy resume is attached. Happy to chat.\n\n${name}`;
  }

  // Professional (default)
  const greeting = company ? `Dear ${company} Hiring Team,` : 'Dear Hiring Manager,';
  const opening =
    `I am writing to express my strong interest in the ${jobTitle} position` +
    (company ? ` at ${company}.` : '.') +
    (summary ? ` ${summary}` : '');
  const expPara = recent && recent.title
    ? `In my most recent role as ${recent.title}${recent.company ? ` at ${recent.company}` : ''}, ${recent.description || 'I contributed to key initiatives and delivered measurable results.'}${totalYears > 0 ? ` Over ${totalYears}+ years in this field, I have developed a comprehensive skill set.` : ''}`
    : 'My background has equipped me with the skills needed to excel in this role.';
  const skillsPara = skills.length
    ? `My core competencies include ${skills.slice(0, 6).join(', ')}, which align with the requirements of this position.`
    : 'My professional background prepares me well for this opportunity.';
  const closing = `I would welcome the opportunity to discuss how my experience can contribute to ${company || 'your team'}.\n\nSincerely,\n${name}`;
  return `${greeting}\n\n${opening}\n\n${expPara}\n\n${skillsPara}\n\n${closing}`;
};

// ──────────────────────────────────────────────────────────────────────────────
// ProfileManagementService
// ──────────────────────────────────────────────────────────────────────────────
class ProfileManagementService {
  constructor() {
    this.apiBaseUrl = DEFAULT_API_BASE;
    this.apiKey = DEFAULT_API_KEY;
    this.authToken = null;
    this.userId = null;
    this.companyId = null;
  }

  // ─── Configuration ─────────────────────────────────────────────────────────
  setApiBaseUrl(url) { this.apiBaseUrl = url; return this; }
  setApiKey(key) { this.apiKey = key; return this; }
  setAuthToken(token) { this.authToken = token; return this; }
  setUserId(id) { this.userId = id; return this; }
  setCompanyId(id) { this.companyId = id; return this; }

  /** Auto-bootstrap from AsyncStorage. Call once at app start, or before first use. */
  async bootstrap() {
    try {
      // Pull every place the user ID could be stored — the app's login flow has
      // historically written to several keys depending on which screen handled signin.
      const [
        authToken, userToken,
        userId, userIdCamel, profUserId, userIdLegacy,
        companyId,
        userDataRaw, userRaw, authRaw,
      ] = await Promise.all([
        AsyncStorage.getItem(PROF_KEYS.AUTH_TOKEN),
        AsyncStorage.getItem(PROF_KEYS.USER_TOKEN),
        AsyncStorage.getItem(PROF_KEYS.USER_ID),         // 'user_id' (snake)
        AsyncStorage.getItem('userId'),                  // camelCase variant
        AsyncStorage.getItem('professional_user_id'),    // BuildResume uses this
        AsyncStorage.getItem('current_user_id'),         // older flow
        AsyncStorage.getItem(PROF_KEYS.COMPANY_ID),
        AsyncStorage.getItem('userData'),
        AsyncStorage.getItem('user'),
        AsyncStorage.getItem('auth_user'),
      ]);

      this.authToken = authToken || userToken || null;
      this.companyId = companyId || null;

      // Resolve a NUMERIC user ID — backend `/api/users/:id/...` endpoints
      // expect an integer matching users.id (bigint). A sliced token string
      // would fail the ownership check we added today.
      const validNumericId = (v) => v != null && !isNaN(parseInt(v, 10)) && parseInt(v, 10) > 0;

      let resolvedId = null;
      for (const candidate of [userId, userIdCamel, profUserId, userIdLegacy]) {
        if (validNumericId(candidate)) { resolvedId = parseInt(candidate, 10); break; }
      }

      // Try JSON-wrapped user objects if direct keys didn't work
      if (!resolvedId) {
        for (const raw of [userDataRaw, userRaw, authRaw]) {
          if (!raw) continue;
          try {
            const obj = JSON.parse(raw);
            const candidate =
              obj?.id ||
              obj?.user_id ||
              obj?.userId ||
              obj?.user?.id ||
              obj?.user?.user_id ||
              obj?.data?.user?.id ||
              obj?.data?.id;
            if (validNumericId(candidate)) {
              resolvedId = parseInt(candidate, 10);
              // Cache it under the canonical key so future reads are faster
              try { await AsyncStorage.setItem(PROF_KEYS.USER_ID, String(resolvedId)); } catch {}
              break;
            }
          } catch {}
        }
      }

      // Last resort: token slice is intentionally NOT used here.
      // It would produce a non-numeric string that always fails ownership checks.
      this.userId = resolvedId;

      return {
        success: true,
        data: {
          userId: this.userId,
          hasToken: !!this.authToken,
          hasNumericId: !!resolvedId,
        },
      };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  // ─── HTTP helpers ──────────────────────────────────────────────────────────
  _headers(extra = {}) {
    return {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
      ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      ...extra,
    };
  }

  async _request(method, endpoint, body = null, isFormData = false) {
    try {
      const headers = isFormData
        ? { 'x-api-key': this.apiKey, ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}) }
        : this._headers();
      const opts = { method, headers };
      if (body) opts.body = isFormData ? body : JSON.stringify(body);
      const res = await fetch(`${this.apiBaseUrl}${endpoint}`, opts);
      if (!res.ok) {
        let msg = res.statusText || `HTTP ${res.status}`;
        try { const j = await res.json(); msg = j.message || j.error || msg; } catch {}
        return { success: false, error: msg, status: res.status };
      }
      const data = await res.json().catch(() => ({}));
      return { success: true, data };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ATS scoring (matches test spec)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Run an ATS test against the backend.
   * @param {object} resumeData
   * @returns {Promise<{success: boolean, data?: {score, summary, keywords, recommendations}, error?: string}>}
   */
  async runATSTest(resumeData) {
    try {
      const res = await fetch(`${this.apiBaseUrl}/api/ats/score`, {
        method: 'POST',
        headers: this._headers(),
        body: JSON.stringify({ resume: resumeData }),
      });

      if (!res.ok) {
        let msg = res.statusText || `HTTP ${res.status}`;
        try {
          const j = await res.json();
          msg = j.message || j.error || msg;
          if (res.status === 401) msg = 'Unauthorized';
        } catch {}
        return { success: false, error: msg };
      }

      const raw = await res.json();
      // Normalize the shape
      const normalized = {
        score: typeof raw.score === 'number' ? raw.score : 0,
        summary: raw.summary || '',
        keywords: {
          matched: Array.isArray(raw.keywords?.matched) ? raw.keywords.matched : [],
          missing: Array.isArray(raw.keywords?.missing) ? raw.keywords.missing : [],
        },
        recommendations: Array.isArray(raw.recommendations) ? raw.recommendations : [],
      };

      // Snapshot to local storage
      if (this.userId) {
        await AsyncStorage.setItem(
          PROF_KEYS.atsResult(this.userId),
          JSON.stringify({ ...normalized, ran_at: new Date().toISOString() })
        );
      }

      return { success: true, data: normalized };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  /**
   * Build prioritized AI recommendation sections from an ATS result + profile context.
   * @param {object} ats - { score, keywords: {matched, missing}, recommendations }
   * @param {object} context - { completionPercentage, ... }
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async buildAIRecommendationsFromATS(ats, context = {}) {
    try {
      const sections = [];

      // 1. Keyword gaps — high priority if many missing
      const missing = Array.isArray(ats?.keywords?.missing) ? ats.keywords.missing : [];
      if (missing.length > 0) {
        sections.push({
          id: 'ats_keywords',
          title: 'Add Missing Keywords',
          priority: missing.length >= 3 ? 'high' : 'medium',
          icon: 'key',
          items: missing.slice(0, 8),
          message: `${missing.length} key term${missing.length === 1 ? '' : 's'} from job postings missing from your profile.`,
        });
      }

      // 2. Profile completion gap — high priority below 50%
      const completion = context.completionPercentage ?? 100;
      if (completion < 80) {
        sections.push({
          id: 'profile_completion',
          title: 'Complete Your Profile',
          priority: completion < 50 ? 'high' : 'medium',
          icon: 'person-circle',
          completion,
          message: `Your profile is ${completion}% complete. Recruiters favor complete profiles.`,
        });
      }

      // 3. ATS score breakdown
      const score = ats?.score ?? 0;
      if (score < 70) {
        sections.push({
          id: 'ats_score',
          title: 'Boost Your ATS Score',
          priority: score < 50 ? 'high' : 'medium',
          icon: 'speedometer',
          score,
          message: `Current ATS score: ${score}/100. Aim for 80+ to clear automated filters.`,
        });
      }

      // 4. Raw recommendations from backend
      const recs = Array.isArray(ats?.recommendations) ? ats.recommendations : [];
      if (recs.length > 0) {
        sections.push({
          id: 'backend_recs',
          title: 'AI Suggestions',
          priority: 'medium',
          icon: 'bulb',
          items: recs,
        });
      }

      // Sort: high priority first
      sections.sort((a, b) => {
        const order = { high: 0, medium: 1, low: 2 };
        return (order[a.priority] ?? 99) - (order[b.priority] ?? 99);
      });

      // Cache for offline use
      if (this.userId) {
        await AsyncStorage.setItem(
          PROF_KEYS.aiRecommendations(this.userId),
          JSON.stringify({ sections, generated_at: new Date().toISOString() })
        );
      }

      return { success: true, data: sections };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  /**
   * Quick local ATS score (no backend) — used as a fallback or instant preview
   * before the full server-side test runs.
   */
  calculateLocalATSScore(resumeData) {
    if (!resumeData) return { score: 0, breakdown: [] };
    const data = resumeData;
    const personalInfo = data.personalInfo || data;
    const skills = Array.isArray(data.skills) ? data.skills : [];
    const experience = Array.isArray(data.experience) ? data.experience : [];
    const education = Array.isArray(data.education) ? data.education : [];
    const projects = Array.isArray(data.projects) ? data.projects : [];
    const certs = Array.isArray(data.certifications) ? data.certifications : [];

    const breakdown = [
      { label: 'Contact Info', score: (personalInfo.name || personalInfo.firstName ? 10 : 0) + (personalInfo.email ? 8 : 0) + (personalInfo.phone ? 7 : 0), max: 25 },
      { label: 'Summary', score: (data.summary || data.professionalSummary || personalInfo.summary) ? 15 : 0, max: 15 },
      { label: 'Skills', score: skills.length >= 5 ? 15 : skills.length > 0 ? 8 : 0, max: 15 },
      { label: 'Experience', score: experience.length >= 3 ? 20 : experience.length > 0 ? 12 : 0, max: 20 },
      { label: 'Education', score: education.length > 0 ? 10 : 0, max: 10 },
      { label: 'Projects / Certs', score: (projects.length ? 8 : 0) + (certs.length ? 7 : 0), max: 15 },
    ];

    const score = breakdown.reduce((sum, b) => sum + b.score, 0);
    return { score: Math.min(score, 100), breakdown };
  }

  /**
   * Generate inline feedback from local analysis (no backend).
   */
  generateLocalFeedback(resumeData) {
    const feedback = [];
    if (!resumeData) return feedback;
    const personalInfo = resumeData.personalInfo || resumeData;
    const skills = Array.isArray(resumeData.skills) ? resumeData.skills : [];
    const experience = Array.isArray(resumeData.experience) ? resumeData.experience : [];

    if (!personalInfo.email) feedback.push({ type: 'error', icon: 'mail', message: 'Missing email — recruiters can\'t contact you.' });
    if (!personalInfo.phone) feedback.push({ type: 'warning', icon: 'call', message: 'No phone number listed.' });
    if (!(resumeData.summary || resumeData.professionalSummary)) feedback.push({ type: 'warning', icon: 'document-text', message: 'Add a 2-3 sentence professional summary.' });
    if (skills.length < 5) feedback.push({ type: 'warning', icon: 'star', message: `Only ${skills.length} skills listed — aim for 8-10 relevant ones.` });
    if (experience.length === 0) feedback.push({ type: 'error', icon: 'briefcase', message: 'No work experience entries.' });
    experience.forEach((e, idx) => {
      if (!e.description && !e.summary) feedback.push({ type: 'tip', icon: 'create', message: `Add bullets to "${e.title || `role ${idx + 1}`}".` });
    });
    feedback.push({ type: 'tip', icon: 'analytics', message: 'Use numbers in achievements (e.g. "Reduced cost by 25%").' });
    return feedback;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Profile (full profile data, used by ProfileScreen)
  // ═══════════════════════════════════════════════════════════════════════════
  async getProfile() {
    try {
      // /api/data/users is access-blocked. Read the rich cache (skills/exp/edu
      // arrays the dashboard loads) and merge fresh /api/me base fields over it,
      // so the AI matcher always receives the full profile.
      let cached = null;
      const cacheKey = this.userId ? PROF_KEYS.profile(this.userId) : null;
      if (cacheKey) {
        const raw = await AsyncStorage.getItem(cacheKey);
        if (raw) { try { cached = JSON.parse(raw); } catch {} }
      }

      const res = await this._request('GET', '/api/me');
      const server = (res.success && res.data)
        ? (res.data.user || res.data.data || res.data)
        : null;

      if (server && (server.id || server.email || server.full_name)) {
        // Merge: start from cache (rich arrays), overlay fresh server scalars.
        // Keep cached arrays whenever the server doesn't supply them.
        const merged = { ...(cached || {}), ...server };
        for (const k of ['skills', 'experience', 'experiences', 'education', 'educations', 'certifications', 'projects']) {
          const sv = server[k];
          if (!Array.isArray(sv) || sv.length === 0) {
            if (cached && Array.isArray(cached[k])) merged[k] = cached[k];
          }
        }
        const uid = this.userId || server.id || server.user_id;
        if (uid) {
          await AsyncStorage.setItem(PROF_KEYS.profile(uid), JSON.stringify(merged));
          await AsyncStorage.setItem(PROF_KEYS.lastProfileSync(uid), new Date().toISOString());
        }
        return { success: true, data: merged, source: 'server+cache' };
      }

      // No server profile — use cache as-is.
      if (cached) return { success: true, data: cached, source: 'cache' };
      return { success: false, error: 'No profile data found' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async saveProfile(profileData) {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      // Cache immediately (local persistence guarantee — UI never loses edits).
      await AsyncStorage.setItem(PROF_KEYS.profile(this.userId), JSON.stringify(profileData));
      // Sync to backend. /api/data/users is access-blocked, so use /api/me.
      const res = await this._request('PUT', '/api/me', profileData);
      await AsyncStorage.setItem(PROF_KEYS.lastProfileSync(this.userId), new Date().toISOString());
      // Treat the local cache write as success even if the server sync is
      // unavailable, so profile editing keeps working offline/first.
      if (res && res.success) return res;
      return { success: true, data: profileData, source: 'cache', serverError: res?.error };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  /**
   * Compute profile completion percentage.
   * Weighted sections — basic info, summary, experience, skills, education are most important.
   */
  getProfileCompletion(profileData) {
    if (!profileData) return 0;
    const data = profileData;
    const personalInfo = data.personalInfo || data;
    const sections = [
      { weight: 15, has: !!(personalInfo.name || personalInfo.firstName) },
      { weight: 10, has: !!personalInfo.email },
      { weight: 5, has: !!personalInfo.phone },
      { weight: 5, has: !!personalInfo.location },
      { weight: 15, has: !!(data.summary || data.professionalSummary || personalInfo.summary) },
      { weight: 20, has: Array.isArray(data.experience) && data.experience.length > 0 },
      { weight: 15, has: Array.isArray(data.skills) && data.skills.length >= 3 },
      { weight: 10, has: Array.isArray(data.education) && data.education.length > 0 },
      { weight: 3, has: Array.isArray(data.projects) && data.projects.length > 0 },
      { weight: 2, has: Array.isArray(data.certifications) && data.certifications.length > 0 },
    ];
    const total = sections.reduce((sum, s) => sum + (s.has ? s.weight : 0), 0);
    return Math.min(total, 100);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Resume library (multiple uploaded resumes per user)
  // ═══════════════════════════════════════════════════════════════════════════
  async listResumes() {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const res = await this._request('GET', `/api/users/${this.userId}/documents?document_type=resume`);
      let list = [];
      if (res.success) list = res.data.documents || res.data.data || res.data.items || res.data || [];
      if (Array.isArray(list)) {
        await AsyncStorage.setItem(PROF_KEYS.resumes(this.userId), JSON.stringify(list));
        return { success: true, data: list };
      }
      // Fall back to cache
      const cached = await AsyncStorage.getItem(PROF_KEYS.resumes(this.userId));
      return { success: true, data: cached ? JSON.parse(cached) : [], source: 'cache' };
    } catch (e) {
      const cached = this.userId ? await AsyncStorage.getItem(PROF_KEYS.resumes(this.userId)) : null;
      return { success: true, data: cached ? JSON.parse(cached) : [], error: e.message, source: 'cache' };
    }
  }

  async uploadResume(file, { documentName } = {}) {
    try {
      if (!this.userId) {
        return {
          success: false,
          error: 'No user ID — please sign out and back in so we can re-establish your account',
        };
      }
      if (isNaN(parseInt(this.userId, 10))) {
        return {
          success: false,
          error: `Invalid user ID (${this.userId}) — sign in again to refresh`,
        };
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'resume');
      if (documentName) formData.append('document_name', documentName);
      const res = await this._request('POST', `/api/users/${this.userId}/documents/upload`, formData, true);
      return res;
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async deleteResume(documentId) {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      return await this._request('DELETE', `/api/users/${this.userId}/documents/${documentId}`);
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async setActiveResume(documentId) {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      // Cache the active-id locally for instant UI feedback
      await AsyncStorage.setItem(PROF_KEYS.activeResumeId(this.userId), String(documentId));
      // Best-effort sync to backend
      return await this._request('POST', `/api/users/${this.userId}/documents/${documentId}/set-active`);
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async getActiveResumeId() {
    if (!this.userId) return null;
    return await AsyncStorage.getItem(PROF_KEYS.activeResumeId(this.userId));
  }

  async saveResumeAnalysis(resumeId, analysis) {
    if (!this.userId) return;
    await AsyncStorage.setItem(
      PROF_KEYS.resumeAnalysis(this.userId, resumeId),
      JSON.stringify(analysis)
    );
  }

  async loadResumeAnalysis(resumeId) {
    if (!this.userId) return null;
    const cached = await AsyncStorage.getItem(PROF_KEYS.resumeAnalysis(this.userId, resumeId));
    return cached ? JSON.parse(cached) : null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Cover letters
  // ═══════════════════════════════════════════════════════════════════════════
  async listCoverLetters() {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const res = await this._request('GET', `/api/users/${this.userId}/documents?document_type=cover_letter`);
      let list = [];
      if (res.success) list = res.data.documents || res.data.data || res.data.items || res.data || [];
      if (Array.isArray(list)) {
        await AsyncStorage.setItem(PROF_KEYS.coverLetters(this.userId), JSON.stringify(list));
        return { success: true, data: list };
      }
      const cached = await AsyncStorage.getItem(PROF_KEYS.coverLetters(this.userId));
      return { success: true, data: cached ? JSON.parse(cached) : [], source: 'cache' };
    } catch (e) {
      const cached = this.userId ? await AsyncStorage.getItem(PROF_KEYS.coverLetters(this.userId)) : null;
      return { success: true, data: cached ? JSON.parse(cached) : [], error: e.message, source: 'cache' };
    }
  }

  /**
   * Generate a cover letter from resume data.
   * @param {string} template - 'professional' | 'modern' | 'creative' | 'brief'
   * @param {object} context - { jobTitle, company }
   * @param {object} resumeData
   * @returns {{ success: true, data: string }}
   */
  generateCoverLetter(template, context, resumeData) {
    try {
      if (!resumeData) return { success: false, error: 'No resume data' };
      if (!context?.jobTitle?.trim()) return { success: false, error: 'Job title required' };
      const facts = extractResumeFacts(resumeData);
      const letter = renderCoverLetterTemplate(template || 'professional', facts, {
        jobTitle: context.jobTitle.trim(),
        company: (context.company || '').trim(),
      });
      return { success: true, data: letter };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async saveCoverLetter(record) {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const id = record.id || `cl_${Date.now()}`;
      const fullRecord = {
        ...record,
        id,
        document_type: 'cover_letter',
        created_at: record.created_at || new Date().toISOString(),
      };

      // Update cache
      const cached = await AsyncStorage.getItem(PROF_KEYS.coverLetters(this.userId));
      const list = cached ? JSON.parse(cached) : [];
      const existingIdx = list.findIndex(c => c.id === id);
      if (existingIdx >= 0) list[existingIdx] = fullRecord;
      else list.unshift(fullRecord);
      await AsyncStorage.setItem(PROF_KEYS.coverLetters(this.userId), JSON.stringify(list));

      // Backend best-effort
      try {
        const formData = new FormData();
        const dataUri = `data:text/plain;base64,${btoaSafe(fullRecord.text || fullRecord.content || '')}`;
        formData.append('file', { uri: dataUri, type: 'text/plain', name: `${fullRecord.document_name || 'cover-letter'}.txt` });
        formData.append('document_type', 'cover_letter');
        formData.append('document_name', fullRecord.document_name || `Cover Letter ${id}`);
        await this._request('POST', `/api/users/${this.userId}/documents/upload`, formData, true);
      } catch (_) {}

      return { success: true, data: fullRecord };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async deleteCoverLetter(id) {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      // Update cache
      const cached = await AsyncStorage.getItem(PROF_KEYS.coverLetters(this.userId));
      const list = cached ? JSON.parse(cached) : [];
      const updated = list.filter(c => c.id !== id);
      await AsyncStorage.setItem(PROF_KEYS.coverLetters(this.userId), JSON.stringify(updated));
      // Backend
      await this._request('DELETE', `/api/users/${this.userId}/documents/${id}`);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Job tailoring
  // ═══════════════════════════════════════════════════════════════════════════
  tailorToJob(jobDescription, resumeData) {
    try {
      if (!jobDescription || jobDescription.trim().length < 30) {
        return { success: false, error: 'Need a fuller job description' };
      }
      if (!resumeData) return { success: false, error: 'No resume data' };
      const facts = extractResumeFacts(resumeData);
      const text = jobDescription.toLowerCase();
      const skills = facts.skills;
      const matched = skills.filter(s => text.includes(s.toLowerCase()));

      // Extract candidate keywords from JD (title-cased or all-caps tokens)
      const tokens = jobDescription
        .split(/[^A-Za-z0-9+/.#\-]+/)
        .filter(t => t.length > 2)
        .filter(t => /^[A-Z]/.test(t) || /^[A-Z]{2,}$/.test(t));
      const unique = Array.from(new Set(tokens.map(t => t.trim()))).slice(0, 30);
      const skillsLower = new Set(skills.map(s => s.toLowerCase()));
      const suggested = unique
        .filter(t => !skillsLower.has(t.toLowerCase()) && t.length > 3)
        .slice(0, 8);

      const powerVerbs = ['lead', 'led', 'built', 'shipped', 'delivered', 'launched', 'scaled', 'designed', 'managed', 'owned', 'drove', 'optimized', 'reduced', 'increased'];
      const verbsInJD = powerVerbs.filter(v => text.includes(v));

      return {
        success: true,
        data: {
          match_percentage: skills.length ? Math.round((matched.length / Math.max(skills.length, 1)) * 100) : 0,
          matched_skills: matched,
          suggested_keywords: suggested,
          power_verbs: verbsInJD,
        },
      };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Portfolio
  // ═══════════════════════════════════════════════════════════════════════════
  async getPortfolio() {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const res = await this._request('GET', `/api/data/portfolio_items?user_id=${this.userId}`);
      if (res.success) {
        const items = res.data.data || res.data.items || res.data || [];
        await AsyncStorage.setItem(PROF_KEYS.portfolioData(this.userId), JSON.stringify(items));
        return { success: true, data: items };
      }
      const cached = await AsyncStorage.getItem(PROF_KEYS.portfolioData(this.userId));
      return { success: true, data: cached ? JSON.parse(cached) : [], source: 'cache' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async savePortfolioItem(item) {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const method = item.id ? 'PUT' : 'POST';
      const endpoint = item.id ? `/api/data/portfolio_items/${item.id}` : `/api/data/portfolio_items`;
      const res = await this._request(method, endpoint, { ...item, user_id: this.userId });
      return res;
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async deletePortfolioItem(id) {
    return await this._request('DELETE', `/api/data/portfolio_items/${id}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Skills (assessments + progress)
  // ═══════════════════════════════════════════════════════════════════════════
  async getSkillAssessments() {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const res = await this._request('GET', `/api/data/skill_assessments?user_id=${this.userId}`);
      if (res.success) {
        const list = res.data.data || res.data.items || res.data || [];
        await AsyncStorage.setItem(PROF_KEYS.skillAssessments(this.userId), JSON.stringify(list));
        return { success: true, data: list };
      }
      const cached = await AsyncStorage.getItem(PROF_KEYS.skillAssessments(this.userId));
      return { success: true, data: cached ? JSON.parse(cached) : [], source: 'cache' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async saveSkillAssessment(assessment) {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const res = await this._request('POST', `/api/data/skill_assessments`, {
        ...assessment,
        user_id: this.userId,
      });
      return res;
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async getSkillProgress() {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const res = await this._request('GET', `/api/data/skill_progress?user_id=${this.userId}`);
      if (res.success) {
        const list = res.data.data || res.data.items || res.data || [];
        await AsyncStorage.setItem(PROF_KEYS.skillProgress(this.userId), JSON.stringify(list));
        return { success: true, data: list };
      }
      const cached = await AsyncStorage.getItem(PROF_KEYS.skillProgress(this.userId));
      return { success: true, data: cached ? JSON.parse(cached) : [], source: 'cache' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async getEnrolledCourses() {
    try {
      if (!this.userId) return { success: false, error: 'No user ID' };
      const res = await this._request('GET', `/api/data/course_enrollments?user_id=${this.userId}`);
      if (res.success) {
        const list = res.data.data || res.data.items || res.data || [];
        await AsyncStorage.setItem(PROF_KEYS.enrolledCourses(this.userId), JSON.stringify(list));
        return { success: true, data: list };
      }
      const cached = await AsyncStorage.getItem(PROF_KEYS.enrolledCourses(this.userId));
      return { success: true, data: cached ? JSON.parse(cached) : [], source: 'cache' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async enrollInCourse(courseId) {
    return await this._request('POST', `/api/data/course_enrollments`, {
      user_id: this.userId,
      course_id: courseId,
      enrolled_at: new Date().toISOString(),
    });
  }
}

// ─── Helper: base64 encoder for binary-safe FormData ──────────────────────────
function btoaSafe(str) {
  if (typeof btoa === 'function') return btoa(unescape(encodeURIComponent(str)));
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
  let output = '';
  const input = unescape(encodeURIComponent(str));
  for (let block = 0, charCode, i = 0, map = chars;
       input.charAt(i | 0) || ((map = '='), i % 1);
       output += map.charAt(63 & (block >> (8 - (i % 1) * 8)))) {
    charCode = input.charCodeAt((i += 3 / 4));
    if (charCode > 0xff) return '';
    block = (block << 8) | charCode;
  }
  return output;
}

// Default singleton instance (most screens can `import ProfileManagementService from ...`)
const instance = new ProfileManagementService();
export default instance;
export { ProfileManagementService };
